import type { AxiosInstance } from "axios";

import { parseAxiosResponse } from "@thalamos/common";
import type {
  CreatePersonRequest,
  MergePersonRequest,
  Person,
  PersonDirectoryPage,
  PersonId,
  PersonMatches,
  PersonMatchRequest,
  PersonSearchRequest,
  UnmergePersonRequest,
  UpdatePersonRequest,
} from "@thalamos/haven-common";
import {
  notificationChannels,
  personChannels,
  PersonDirectoryPageSchema,
  PersonMatchesSchema,
  PersonSchema,
} from "@thalamos/haven-common";

import type { ErrorBody, ResponseBody } from "../axios/api.js";
import { useLiveApiAxios } from "../axios/api.js";

const parseResponsePerson = parseAxiosResponse(PersonSchema);
const parseResponsePersonPage = parseAxiosResponse(PersonDirectoryPageSchema);
const parseResponseMatches = parseAxiosResponse(PersonMatchesSchema);

/**
 * Only the fields a caller actually named are sent, so the server's defaults
 * stay the single definition of a page size.
 */
const searchBody = ({
  query,
  limit,
  offset,
  sort,
  filters,
  onlyUnassigned,
  withFacets,
  withUnassignedCount,
}: PersonSearchRequest) => ({
  ...(query !== undefined && query.length > 0 ? { query } : {}),
  ...(limit !== undefined ? { limit } : {}),
  ...(offset !== undefined ? { offset } : {}),
  ...(sort !== undefined ? { sort } : {}),
  ...(filters !== undefined ? { filters } : {}),
  ...(onlyUnassigned === true ? { onlyUnassigned } : {}),
  ...(withFacets === true ? { withFacets } : {}),
  ...(withUnassignedCount === true ? { withUnassignedCount } : {}),
});

/**
 * Every response carrying a person is parsed like the reads, so the declared
 * type is one a caller may act on. `parseAxiosResponse` validates only a 200,
 * which leaves a refusal's body to reach the caller as the error shape it is —
 * and leaves a 201 unparsed, so the two creating calls parse on the way out.
 */
export const config = {
  person: {
    // A POST although it changes nothing — see
    // docs/decisions/2026-10-12-a-young-person-search-sends-its-terms-in-the-body.md.
    search: (request: PersonSearchRequest) => ({
      url: "/api/persons/searches",
      method: "post",
      data: searchBody(request),
      transformResponse: parseResponsePersonPage,
    }),
    match: (body: PersonMatchRequest) => ({
      url: "/api/persons/matches",
      method: "post",
      data: body,
      transformResponse: parseResponseMatches,
    }),
    byId: (personId: string) => ({
      url: `/api/persons/${personId}`,
      method: "get",
      transformResponse: parseResponsePerson,
    }),
    create: (body: CreatePersonRequest) => ({
      url: "/api/persons",
      method: "post",
      data: body,
    }),
    replace: (personId: PersonId, body: UpdatePersonRequest) => ({
      url: `/api/persons/${personId}`,
      method: "put",
      data: body,
      transformResponse: parseResponsePerson,
    }),
    /** Declares the record in the body to be the same person as `targetPersonId`. */
    merge: (targetPersonId: PersonId, body: MergePersonRequest) => ({
      url: `/api/persons/${targetPersonId}/merge`,
      method: "post",
      data: body,
    }),
    unmerge: ({
      targetPersonId,
      sourcePersonId,
      request,
    }: {
      targetPersonId: PersonId;
      sourcePersonId: PersonId;
      request: UnmergePersonRequest;
    }) => ({
      url: `/api/persons/${targetPersonId}/merges/${sourcePersonId}/unmerge`,
      method: "post",
      data: request,
      transformResponse: parseResponsePerson,
    }),
    archive: (personId: PersonId) => ({
      url: `/api/persons/${personId}/archive`,
      method: "post",
    }),
    unarchive: (personId: PersonId) => ({
      url: `/api/persons/${personId}/unarchive`,
      method: "post",
    }),
  },
};

/**
 * A created record answers 201, which `parseAxiosResponse` leaves alone, so the
 * body is parsed here when it arrives. A refusal keeps its error body.
 */
const parseCreated = (response: {
  status: number;
  data: unknown;
}): ResponseBody<Person, ErrorBody> =>
  response.status === 201
    ? PersonSchema.parse(response.data)
    : (response.data as ErrorBody);

export const api = (apiAxios: AxiosInstance) => {
  return {
    person: {
      search: (request: PersonSearchRequest) =>
        apiAxios.request<ResponseBody<PersonDirectoryPage, ErrorBody>>(
          config.person.search(request),
        ),
      match: (body: PersonMatchRequest) =>
        apiAxios.request<ResponseBody<PersonMatches, ErrorBody>>(
          config.person.match(body),
        ),
      byId: (personId: string) =>
        apiAxios.request<ResponseBody<Person, ErrorBody>>(
          config.person.byId(personId),
        ),
      create: async (body: CreatePersonRequest) => {
        const response = await apiAxios.request<unknown>(
          config.person.create(body),
        );

        return { status: response.status, data: parseCreated(response) };
      },
      replace: (personId: PersonId, body: UpdatePersonRequest) =>
        apiAxios.request<ResponseBody<Person, ErrorBody>>(
          config.person.replace(personId, body),
        ),
      merge: async (targetPersonId: PersonId, body: MergePersonRequest) => {
        const response = await apiAxios.request<unknown>(
          config.person.merge(targetPersonId, body),
        );

        return { status: response.status, data: parseCreated(response) };
      },
      unmerge: (unmerge: Parameters<typeof config.person.unmerge>[0]) =>
        apiAxios.request<ResponseBody<Person, ErrorBody>>(
          config.person.unmerge(unmerge),
        ),
      archive: (personId: PersonId) =>
        apiAxios.request<unknown>(config.person.archive(personId)),
      unarchive: (personId: PersonId) =>
        apiAxios.request<unknown>(config.person.unarchive(personId)),
    },
  };
};

export const apiHooks = () => {
  return {
    person: {
      /**
       * A page of young people.
       *
       * `watchesDirectoryOf` names an organisation whose directory this listing
       * should follow, so a record registered, corrected, merged or split back
       * out by a colleague appears without a reload. Omit it for a listing that
       * must not move under the caller — picking a record to merge into is the
       * case in point, where rows reordering mid-selection is worse than rows
       * being a few seconds old.
       */
      search: function usePersonSearch(
        request: PersonSearchRequest,
        watchesDirectoryOf?: string,
        options?: { manual?: boolean },
      ) {
        return useLiveApiAxios<PersonDirectoryPage>(
          watchesDirectoryOf === undefined
            ? undefined
            : [notificationChannels.personDirectory(watchesDirectoryOf)],
          config.person.search(request),
          options,
        );
      },

      /**
       * Live on every record in this young person's merge tree.
       *
       * Watching starts from the id asked for and widens to the tree once the
       * response names it. Both halves matter: the request's id covers a merge
       * landing while the page is still loading, and the tree covers the case a
       * route-derived channel gets wrong — a record that has been merged away
       * answers with the record it now belongs to, whose id is not the one
       * asked for.
       *
       * The requested id repeats once the response names it too. Channels are
       * kept by name, so the duplicate is one channel.
       */
      byId: function usePersonById(personId: string) {
        return useLiveApiAxios<Person>(
          (person) => [
            notificationChannels.person(personId),
            ...(person === undefined ? [] : personChannels(person)),
          ],
          config.person.byId(personId),
        );
      },
    },
  };
};
